// Como uma duração vira texto, e como a tela sabe que o dia virou. Duas
// formas da mesma duração: a curta, que vai na tela, e a por extenso, que vai
// no `aria-label` — "1h07" o VoiceOver lê como "um agá zero sete".
//
// Nada aqui sabe de sessão nem de banco: a duração em minutos vem de
// `duracaoMinutos` (domínio), que a calcula de `inicio` e do relógio.

import { limitesDoDia } from '@sheafyield/dominio';
import { useEffect, useState } from 'react';

/** Minutos inteiros e não negativos. Um valor quebrado ou fora de faixa
 *  (relógio do aparelho que andou para trás, `NaN` de uma conta com data
 *  inválida) vira zero em vez de "-1min" ou "NaNmin" na tela. */
function minutosValidos(minutos: number): number {
  if (!Number.isFinite(minutos)) return 0;
  return Math.max(0, Math.floor(minutos));
}

/**
 * O mostrador: `"0min"`, `"48min"`, `"1h07"`. Abaixo de uma hora só os
 * minutos; de uma hora em diante, horas e minutos com dois dígitos — é o
 * que mantém a largura estável de um minuto para o outro (com
 * `tabular-nums`, "1h07" e "1h08" ocupam exatamente o mesmo espaço, e o
 * número não dança na tela a cada minuto).
 */
export function formatarDuracao(minutos: number): string {
  const total = minutosValidos(minutos);
  if (total < 60) return `${String(total)}min`;
  const horas = Math.floor(total / 60);
  const resto = total % 60;
  return `${String(horas)}h${String(resto).padStart(2, '0')}`;
}

function plural(quantidade: number, singular: string, plural: string): string {
  return `${String(quantidade)} ${quantidade === 1 ? singular : plural}`;
}

/**
 * A mesma duração para quem ouve: `"48 minutos"`, `"1 hora e 7 minutos"`,
 * `"2 horas"`. Sem o "e" quando os minutos são zero, que é como se fala.
 */
export function duracaoPorExtenso(minutos: number): string {
  const total = minutosValidos(minutos);
  const horas = Math.floor(total / 60);
  const resto = total % 60;
  if (horas === 0) return plural(resto, 'minuto', 'minutos');
  const emHoras = plural(horas, 'hora', 'horas');
  if (resto === 0) return emHoras;
  return `${emHoras} e ${plural(resto, 'minuto', 'minutos')}`;
}

/**
 * Quanto falta, em milissegundos, para o mostrador mudar de minuto.
 *
 * O alvo não é a virada do minuto do relógio de parede: é `inicio + k × 60
 * s`, que é onde `duracaoMinutos` muda de valor. Alinhar o tique a isto
 * troca sessenta repinturas por uma, e cada repintura arrasta a árvore
 * inteira do cronômetro.
 *
 * Os 50 ms de folga não são superstição: um `setTimeout` que dispare um fio
 * de segundo cedo deixaria o mostrador no minuto anterior e reagendaria para
 * daqui a quase nada, virando laço apertado.
 */
export function esperaAteOProximoMinuto(
  inicioMs: number,
  agoraMs: number,
): number {
  const decorrido = agoraMs - inicioMs;
  // O resto de um número negativo é negativo em JavaScript, e um relógio que
  // andou para trás produz exatamente isso.
  const resto = ((decorrido % 60_000) + 60_000) % 60_000;
  return 60_000 - resto + 50;
}

/**
 * Quando o primeiro tique acontece — a única coisa em que o cronômetro e o
 * total do dia discordam, e por isso a única coisa que este hook pede que
 * lhe digam.
 *
 * `'ao montar'` marca assim que o efeito entra em cena — ao montar e a cada
 * troca de sessão. É o do cronômetro: o mostrador **é** o relógio, e a
 * leitura de que ele depende tem de ser refeita junto com a sessão. Sem
 * isso, pausar e retomar mostraria a sessão nova com a leitura de relógio da
 * anterior, até a virada seguinte.
 *
 * `'na virada'` só marca na virada. É o dos totais: eles vêm da releitura do
 * banco, e a consulta acabou de rodar com a chave atual. Marcar ao entrar em
 * cena trocaria a chave e mandaria reler o que já se leu — a cada montagem
 * da tela e a cada troca de sessão.
 *
 * Voltar do segundo plano marca sempre, nos dois casos: o temporizador
 * agendado antes de o iOS congelar a aba pode nunca ter disparado, e aí não
 * há valor de que desconfiar — há valor de horas atrás.
 */
export type PrimeiroTique = 'ao montar' | 'na virada';

/**
 * Um número que anda **uma vez por minuto enquanto houver sessão aberta**,
 * alinhado ao `inicio` dela. `null` em `inicioMs` é "não há sessão": o
 * número para de andar, porque sem sessão não há nada que mude sozinho.
 *
 * O que se ganha alinhando ao `inicio`, e não à virada do minuto do relógio
 * de parede: o mostrador do cronômetro vira em `inicio + k × 60 s`, que é
 * onde `duracaoMinutos` muda de valor. Fosse o relógio de parede, o
 * cronômetro diria "1h08" com o total do dia ainda em "1h07" — o tipo de
 * desencontro que faz duvidar do número. E é um tique por minuto, não um por
 * segundo: o mostrador só mostra minutos, e sessenta repinturas da árvore
 * para cada mudança visível é bateria queimada à toa num aparelho com o
 * texto no tamanho de acessibilidade.
 *
 * Com a aba escondida não há tique: o iOS congela o temporizador de qualquer
 * jeito, e um tique invisível só gastaria bateria. Ao voltar não há nada a
 * repor — o valor vem do banco e do relógio, então basta remarcar.
 *
 * O que volta é **contador**, não relógio: dois tiques no mesmo
 * milissegundo (uma troca rápida de visibilidade) dariam o mesmo instante, e
 * um valor que não muda é uma releitura que não acontece. Quem precisa da
 * hora a lê do relógio quando o contador anda.
 */
export function useViradaDeMinuto(
  inicioMs: number | null,
  primeiroTique: PrimeiroTique,
): number {
  const [virada, setVirada] = useState(0);
  useEffect(() => {
    if (inicioMs === null) return;
    // Cópia em `const`: o TypeScript não leva o estreitamento de um
    // parâmetro para dentro dos fechamentos abaixo, e `agendar` precisa do
    // número, não de `number | null`.
    const inicio = inicioMs;
    let agendado: ReturnType<typeof setTimeout> | undefined;
    function parar(): void {
      if (agendado !== undefined) clearTimeout(agendado);
      agendado = undefined;
    }
    function agendar(): void {
      agendado = setTimeout(
        () => {
          setVirada((n) => n + 1);
          agendar();
        },
        esperaAteOProximoMinuto(inicio, Date.now()),
      );
    }
    function comecar(marcarAgora: boolean): void {
      if (marcarAgora) setVirada((n) => n + 1);
      agendar();
    }
    function aoMudarVisibilidade(): void {
      parar();
      if (document.hidden) return;
      comecar(true);
    }
    if (!document.hidden) comecar(primeiroTique === 'ao montar');
    document.addEventListener('visibilitychange', aoMudarVisibilidade);
    return () => {
      parar();
      document.removeEventListener('visibilitychange', aoMudarVisibilidade);
    };
  }, [inicioMs, primeiroTique]);
  return virada;
}

/** A meia-noite local do dia que contém `quando`, em milissegundos. É o que
 *  transforma dois instantes em dois **dias de calendário do usuário**
 *  (ADR-0003): comparar `Date.now()` cru contaria 23 h como "ontem" e 25 h
 *  como "há 1 dia", e quem agendou para ontem às 18h veria "hoje" a manhã
 *  inteira. */
function meiaNoiteLocal(quando: Date): number {
  return new Date(
    quando.getFullYear(),
    quando.getMonth(),
    quando.getDate(),
  ).getTime();
}

/**
 * Há quanto tempo, em dias de calendário: `"hoje"`, `"há 3 dias"`,
 * `"há 2 semanas"`, `"há 5 meses"`.
 *
 * É a meta da linha da tira "Para visitar hoje", onde o instante comparado é
 * a data agendada: quem estava marcado para anteontem lê "há 2 dias", que é
 * a razão de ele aparecer antes na lista. Data no futuro (ou inválida) não
 * tem "há" nenhum e vira `"hoje"` — a consulta já não devolve ninguém de
 * amanhã, e mostrar "há -1 dias" seria pior que arredondar para hoje.
 *
 * O `Math.round` não é folga: entre duas meia-noites locais pode haver 23 h
 * ou 25 h onde ainda existe horário de verão, e a divisão por 86 400 000
 * daria 0,958 dia.
 */
export function haQuantoTempo(iso: string, agora: Date): string {
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return 'hoje';
  const dias = Math.round(
    (meiaNoiteLocal(agora) - meiaNoiteLocal(quando)) / 86_400_000,
  );
  if (dias <= 0) return 'hoje';
  if (dias < 7) return `há ${plural(dias, 'dia', 'dias')}`;
  // A escada existe para o número continuar legível de relance: "há 96 dias"
  // se lê contando, "há 3 meses" se lê de uma vez. Os degraus são grosseiros
  // de propósito (semana de 7 dias, mês de 30) — a meta da linha diz *há
  // quanto tempo*, não a data exata, que está na ficha da pessoa.
  if (dias < 28)
    return `há ${plural(Math.floor(dias / 7), 'semana', 'semanas')}`;
  return `há ${plural(Math.max(1, Math.floor(dias / 30)), 'mês', 'meses')}`;
}

/**
 * A hora local de um instante ISO, `"09:42"`, para a meta das visitas do
 * dia. Sai com dois dígitos nas duas partes, que é o que mantém a coluna
 * alinhada com `tabular-nums`.
 */
export function horaLocal(iso: string): string {
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return '';
  return quando.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * O dia local de `agora`, como texto estável: a meia-noite local em ISO com
 * `Z`, exatamente o `de` de `limitesDoDia`. Não é data para mostrar — é
 * **chave**: é o que faz `useConsulta` reler quando o dia vira.
 *
 * Sai de `limitesDoDia` de propósito, e não de um `toISOString().slice(0,
 * 10)`: o dia do usuário começa à meia-noite dele (ADR-0003), e é esse o
 * mesmo recorte que as consultas usam. Duas fontes para "que dia é hoje"
 * discordariam justamente na hora em que a diferença aparece.
 */
export function diaLocal(agora: Date): string {
  return limitesDoDia(agora).de;
}

/**
 * Quanto falta, em milissegundos, para a próxima meia-noite local.
 *
 * Os 50 ms de folga são os mesmos de `esperaAteOProximoMinuto`: um
 * `setTimeout` que dispare um fio de segundo cedo recalcularia o mesmo dia e
 * reagendaria para daqui a nada, virando laço apertado. E é o horário de
 * verão que obriga a perguntar ao calendário em vez de somar 86 400 000:
 * uma noite de virada tem 23 h ou 25 h.
 */
export function esperaAteAProximaMeiaNoite(agoraMs: number): number {
  const agora = new Date(agoraMs);
  const proxima = new Date(
    agora.getFullYear(),
    agora.getMonth(),
    agora.getDate() + 1,
  ).getTime();
  return Math.max(50, proxima - agoraMs + 50);
}

/**
 * O dia local corrente, que **anda sozinho à meia-noite**.
 *
 * Sem isto a tela Hoje mente depois da virada: as consultas só releem por
 * mutação local ou por fim de ciclo do motor, e um aparelho parado na
 * mesinha de cabeceira não tem nem uma coisa nem outra — "Visitas de hoje"
 * continuaria listando as de ontem, e o cartão "Hoje", os minutos de ontem,
 * até alguém escrever alguma coisa. O tique por minuto do cronômetro também
 * não salva: sem sessão aberta ele não existe.
 *
 * O valor devolvido entra na chave das três consultas do dia. Trocar de dia
 * troca a chave, e trocar a chave é a única alavanca que releitura tem.
 *
 * `visibilitychange` não é luxo: o iOS congela os temporizadores da aba
 * escondida, e o caso comum é justamente esse — o app fica em segundo plano
 * a noite inteira e volta de manhã. Ao voltar, a primeira coisa é reperguntar
 * que dia é hoje, porque o temporizador agendado ontem pode nunca ter
 * disparado.
 */
export function useDiaLocal(): string {
  const [dia, setDia] = useState(() => diaLocal(new Date()));
  useEffect(() => {
    let agendado: ReturnType<typeof setTimeout> | undefined;
    function parar(): void {
      if (agendado !== undefined) clearTimeout(agendado);
      agendado = undefined;
    }
    function agendar(): void {
      agendado = setTimeout(() => {
        setDia(diaLocal(new Date()));
        agendar();
      }, esperaAteAProximaMeiaNoite(Date.now()));
    }
    function aoMudarVisibilidade(): void {
      parar();
      if (document.hidden) return;
      setDia(diaLocal(new Date()));
      agendar();
    }
    if (!document.hidden) agendar();
    document.addEventListener('visibilitychange', aoMudarVisibilidade);
    return () => {
      parar();
      document.removeEventListener('visibilitychange', aoMudarVisibilidade);
    };
  }, []);
  return dia;
}
